import {AirlineModel} from './AirlineModel';
import {ImageSourceSetDto} from './DestinationImagesModel';

export enum RouteType {
    Domestic = 'Domestic', International = 'International'
}

export interface ShareDealView {
    id: number;
    departureIata: string;
    destinationIata: string;
    standardPrice: number;
    priceMin: number;
    flightClass: FlightClass;
    destinationCityName: string;
    destinationCountry: string;
    departureCityName: string;
    cover: ImageSourceSetDto;
    destinationCityId: number;
    destinationStateName?: string;
    dealDates: DealDateDto[];
}

export interface DealDateDto {
    startDate: string;
    endDate: string;
}

export enum FlightClass {
    Economy = 'economy',
    PremiumEconomy = 'premium_economy',
    Business = 'business',
    FirstClass = 'first',
}

export const FLIGHT_CLASS_LABEL: Record<FlightClass, string> = {
    [FlightClass.Economy]: 'Economy',
    [FlightClass.PremiumEconomy]: 'Premium Economy',
    [FlightClass.Business]: 'Business',
    [FlightClass.FirstClass]: 'First Class',
};

export function getFlightClassLabel(fc: FlightClass): string {
    return FLIGHT_CLASS_LABEL[fc];
}

export interface CoordinatesDto {
    latitude: number;
    longitude: number;
}

export interface DealDestinationDto {
    cityId: number;
    iata: string;
    cityName: string;
    stateName?: string;
    country: string;
    isDreamDestination: boolean;
    coordinates: CoordinatesDto;
    description?: string;
}

export interface DealCardDepartureDto {
    iata: string;
    coordinates: CoordinatesDto;
}

export interface DealDepartureDto {
    iata: string;
    cityName: string;
}

export interface DashboardDealCardDto {
    id: number;
    destination: DealDestinationDto;
    departure: DealCardDepartureDto;
    coverImage: ImageSourceSetDto;
    priceMin: number;
    priceMax: number;
    standardPrice: number;
    flightClass: FlightClass;
    minimumTier: number;
    routeType: RouteType;
    currentUserGotDeal: boolean;
    airlineIds: number[];
    isExpired: boolean;
    dealAgeDays: number;
    createdAt: string;
}

export interface DashboardDepartureOptionDto {
    id: number;
    name: string;
    code: string;
    cityName: string;
    cityId: number;
    coverImage: ImageSourceSetDto;
}

export interface DashboardDestinationOptionDto {
    id: number;
    name: string;
    stateName: string | null;
    countryName: string;
    coverImage: ImageSourceSetDto;
}

export interface DealDashboardListDto {
    deals: DashboardDealCardDto[];
    filterOptions: {
        airlines: AirlineModel[];
        usersDestinations: DashboardDestinationOptionDto[];
        usersDepartures: DashboardDepartureOptionDto[];
    };
}

export interface DealItinerarySummaryDto {
    airlineNames: string[];
    startDate: string;
    endDate: string;
    outboundStops: number;
    inboundStops: number;
    numberOfStops: number;
    priceMin: number;
    priceMax?: number;
    bookingLink: string;
    // Per-date deal ceiling (the month-specific threshold the date qualified under).
    // TODO: make non-optional once the backend persists and returns the per-date threshold.
    thresholdPrice?: number;
}

export interface FeaturedItineraryDto extends DealItinerarySummaryDto {
    standardPrice: number;
    bestPriceForRouteInLastYear: number;
}

export type AlternateItineraryDto = DealItinerarySummaryDto;

export interface ItineraryDealView extends DealItinerarySummaryDto {
    airline: string;
    dates: string;
    stops: string;
    price: number;
    destinationIata: string;
    departureIata: string;
}

export interface DealDetailDto {
    id: number;
    flightClass: FlightClass;
    minimumTier: number;
    destination: DealDestinationDto;
    departure: DealDepartureDto;
    dealTimeframe: string;
    discount: number;
    dealAgeDays: number;
    destinationImageGallery: ImageSourceSetDto[];
    destinationRanking?: number;
    viatorDestinationId: number | null;
    hasMemberDiscount: boolean;
    memberDiscountCode: string | null;
    featuredItinerary: FeaturedItineraryDto;
    alternateItineraries: AlternateItineraryDto[];
    additionalDealsWithCover: DashboardDealCardDto[];
    usersDestinations: DashboardDestinationOptionDto[];
}

export interface DealDetailStaticDto {
    id: number;
    flightClass: FlightClass;
    minimumTier: number;
    destination: DealDestinationDto;
    departure: DealDepartureDto;
    dealAgeDays: number;
    isExpired: boolean;
    destinationImageGallery: ImageSourceSetDto[];
    destinationRanking?: number;
    viatorDestinationId: number | null;
    hasMemberDiscount: boolean;
    memberDiscountCode: string | null;
    additionalDealsWithCover: DashboardDealCardDto[];
    usersDestinations: DashboardDestinationOptionDto[];
    // Cached pricing data from deal_price_snapshot
    dealTimeframe: string;
    discount: number;
    featuredItinerary: FeaturedItineraryDto;
    alternateItineraries: AlternateItineraryDto[];
}

export interface DealDetailPricingDto {
    dealId: number;
    dealTimeframe: string;
    discount: number;
    featuredItinerary: FeaturedItineraryDto;
    alternateItineraries: AlternateItineraryDto[];
}
